import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const WARNING_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
const CHECK_INTERVAL_MS = 30_000; // check every 30s

export function useSessionWarning() {
  const { session } = useAuth();
  const [minutesLeft, setMinutesLeft] = useState<number | null>(null);
  const [isRenewing, setIsRenewing] = useState(false);
  const [renewed, setRenewed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const renewedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!session) {
      setMinutesLeft(null);
      setDismissed(false);
      return;
    }

    const check = () => {
      const expiresAtMs = (session.expires_at ?? 0) * 1000;
      const timeLeftMs = expiresAtMs - Date.now();

      if (timeLeftMs > 0 && timeLeftMs <= WARNING_THRESHOLD_MS) {
        const mins = Math.ceil(timeLeftMs / 60_000);
        setMinutesLeft(mins);
        setDismissed(false);
      } else {
        setMinutesLeft(null);
      }
    };

    check();
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [session]);

  // When session is refreshed, clear the warning
  useEffect(() => {
    setMinutesLeft(null);
    setDismissed(false);
  }, [session?.expires_at]);

  const renewNow = useCallback(async () => {
    setIsRenewing(true);
    try {
      const { error } = await supabase.auth.refreshSession();
      if (!error) {
        setMinutesLeft(null);
        setRenewed(true);
        if (renewedTimerRef.current) clearTimeout(renewedTimerRef.current);
        renewedTimerRef.current = setTimeout(() => setRenewed(false), 3500);
      }
    } finally {
      setIsRenewing(false);
    }
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  const showWarning = !dismissed && minutesLeft !== null && minutesLeft <= 5;

  return {
    showWarning,
    minutesLeft,
    isRenewing,
    renewed,
    renewNow,
    dismiss,
  };
}
